import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { ChevronLeft, Clock, Gem, ChevronRight, Loader2, FileText, Video, Music, Image as ImageIcon, User, Globe } from "lucide-react";

const CONTENT_ICON = { audio: Music, video: Video, image: ImageIcon, text: FileText };
const LOCALE_TAG = { de: "de-DE", en: "en-US", es: "es-ES", fr: "fr-FR" };

function relativeTime(iso, locale) {
  if (!iso) return "";
  try {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    if (diff < 60) return rtf.format(-diff, "second");
    if (diff < 3600) return rtf.format(-Math.floor(diff / 60), "minute");
    if (diff < 86400) return rtf.format(-Math.floor(diff / 3600), "hour");
    const d = Math.floor(diff / 86400);
    if (d < 30) return rtf.format(-d, "day");
    return rtf.format(-Math.floor(d / 30), "month");
  } catch { return ""; }
}

export default function UnlockedPosts() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("unlocked_posts_title"));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const localeTag = LOCALE_TAG[lang] || "en-US";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/feed/my-unlocked-posts");
      setRows(Array.isArray(data) ? data : []);
    } catch { setRows([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="unlocked-posts">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="up-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <div>
          <h1 className="font-display text-2xl text-foreground">{t("unlocked_posts_title")}</h1>
          <p className="text-sm text-muted-foreground">{t("unlocked_posts_sub")}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border bg-secondary/40 p-8 text-center">
          <FileText className="h-8 w-8 text-muted-foreground/60" />
          <p className="font-display text-base text-foreground">{t("unlocked_posts_empty_title")}</p>
          <p className="max-w-sm text-sm text-muted-foreground">{t("unlocked_posts_empty_body")}</p>
          <button onClick={() => navigate("/app/feed")} data-testid="up-empty-cta" className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"><Globe className="h-3.5 w-3.5" /> {t("unlocked_posts_go_feed")}</button>
        </div>
      ) : (
        <div className="space-y-2.5">
          {rows.map((p) => {
            const Icon = CONTENT_ICON[p.content_type] || FileText;
            return (
              <button key={p.id} data-testid={`up-open-${p.id}`} onClick={() => navigate(`/app/feed/post/${p.id}`)}
                className="flex w-full items-center gap-3 rounded-xl border border-border bg-secondary/60 p-3.5 text-left transition-colors hover:bg-accent">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15"><Icon className="h-5 w-5 text-primary" /></span>
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-2 text-sm font-semibold text-foreground">{p.title || (p.body ? p.body.slice(0, 60) + "…" : "Post")}</span>
                  {p.coach?.name ? <span className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"><User className="h-3 w-3" /> {p.coach.name}</span> : null}
                  <span className="mt-1.5 flex flex-wrap gap-1.5">
                    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-medium text-muted-foreground"><Clock className="h-2.5 w-2.5" />{relativeTime(p.unlocked_at, localeTag)}</span>
                    {p.credits_spent > 0 ? <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/15 px-2 py-0.5 text-[10px] font-medium text-primary"><Gem className="h-2.5 w-2.5" />{p.credits_spent} Credits</span> : null}
                  </span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
